import { useState } from "react";
import { Activity, Monitor, KeyRound } from "lucide-react";
import {
  Badge,
  Button,
  DialogActions,
  Field,
  Select,
  SegmentedControl,
} from "../../design-system";
import {
  UsageExplorer,
  UsageExplorerPreview,
  type UsageExplorerProps,
} from "./usage-explorer";
import type { UsageExplorerExample } from "./usage-explorer-data";
import { RelayStatus, aiError, usd } from "./llm-keys";
import {
  aiUsageFixture,
  initialAiFilters,
  machineAiFixture,
  type AiCall,
  type AiExample,
  type AiFilters,
  type AiUsageView,
  type MachineAiView,
  type AiMode,
} from "./llm-data";
import "./llm.css";
const n = (v: number) => v.toLocaleString("ko-KR");
const duration = (v: number | null) =>
  v === null ? "—" : `${(v / 1000).toFixed(2)}초`;
const when = (v: string) =>
  new Intl.DateTimeFormat("ko-KR", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Seoul",
  }).format(new Date(v));
function CallStatus({ call }: { call: AiCall }) {
  return (
    <Badge
      tone={
        call.status === "ok"
          ? "teal"
          : call.status === "error"
            ? "coral"
            : "neutral"
      }
    >
      {{ ok: "성공", error: "실패", cancelled: "취소" }[call.status]}
    </Badge>
  );
}
export function AiCallList({
  calls,
  onOpenRun,
  compact = false,
}: {
  calls: AiCall[];
  onOpenRun?: (call: AiCall) => void;
  compact?: boolean;
}) {
  const task = (c: AiCall) =>
    onOpenRun && c.runId ? (
      <button className="lai-link" onClick={() => onOpenRun(c)}>
        {c.taskTitle ?? "실행 상세"}
      </button>
    ) : (
      <strong>{c.taskTitle ?? "연결된 작업 없음"}</strong>
    );
  const tokens = (c: AiCall) => (
    <dl className="lai-tokens">
      <div>
        <dt>입력</dt>
        <dd>{n(c.inputTokens)}</dd>
      </div>
      <div>
        <dt>캐시 입력</dt>
        <dd>{n(c.cachedTokens)}</dd>
      </div>
      <div>
        <dt>출력</dt>
        <dd>{n(c.outputTokens)}</dd>
      </div>
      <div>
        <dt>추론</dt>
        <dd>{n(c.reasoningTokens)}</dd>
      </div>
    </dl>
  );
  if (!calls.length)
    return (
      <div className="lai-empty">
        <Activity size={30} />
        <h3>AI 호출 이력이 없습니다</h3>
        <p>선택한 조건을 바꾸거나 작업이 실행된 뒤 확인해 주세요.</p>
      </div>
    );
  return (
    <div data-ai-calls>
      <div
        className="lai-call-table"
        tabIndex={0}
        role="region"
        aria-label="AI 호출 이력 표"
      >
        <table>
          <thead>
            <tr>
              <th>시각 / 작업</th>
              {!compact && <th>고객사 / 실행 위치</th>}
              <th>방식 / 모델</th>
              <th>토큰</th>
              <th>비용 USD</th>
              <th>응답 시간</th>
              <th>상태</th>
            </tr>
          </thead>
          <tbody>
            {calls.map((c) => (
              <tr key={c.id} className={c.status === "error" ? "is-error" : ""}>
                <td>
                  <small>{when(c.startedAt)}</small>
                  {task(c)}
                </td>
                {!compact && (
                  <td>
                    <strong>{c.customerName}</strong>
                    {c.targetCustomerName &&
                      c.targetCustomerName !== c.customerName && (
                        <small>대상 · {c.targetCustomerName}</small>
                      )}
                    <small>
                      {c.machineLabel} · {c.deviceSerial ?? "디바이스 없음"}
                    </small>
                  </td>
                )}
                <td>
                  <Badge>{c.mode === "relay" ? "API 키" : "로컬 세션"}</Badge>
                  <small>{c.model}</small>
                  <small>
                    {c.mode === "local"
                      ? "작업 합계"
                      : (c.keyLabel ?? "삭제된 키")}
                  </small>
                </td>
                <td>{tokens(c)}</td>
                <td>
                  <strong>{usd(c.costUsd)}</strong>
                </td>
                <td>
                  <small>첫 응답 {duration(c.firstByteMs)}</small>
                  <small>전체 {duration(c.durationMs)}</small>
                </td>
                <td>
                  <CallStatus call={c} />
                  {c.error && (
                    <details className="lai-call-error">
                      <summary>오류 보기</summary>
                      <p>{c.error}</p>
                    </details>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lai-call-cards">
        {calls.map((c) => (
          <article
            key={c.id}
            className={`lai-panel ${c.status === "error" ? "is-error" : ""}`}
          >
            <div className="lai-inline">
              <span>{when(c.startedAt)}</span>
              <CallStatus call={c} />
            </div>
            {task(c)}
            {!compact && (
              <p className="lai-muted">
                {c.customerName}
                {c.targetCustomerName && c.targetCustomerName !== c.customerName
                  ? ` → ${c.targetCustomerName}`
                  : ""}
                <br />
                {c.machineLabel} · {c.deviceSerial ?? "디바이스 없음"}
              </p>
            )}
            <div className="lai-inline">
              <Badge>
                {c.mode === "relay" ? "API 키" : "로컬 세션 · 작업 합계"}
              </Badge>
              <span>{c.model}</span>
              <strong>{usd(c.costUsd)}</strong>
            </div>
            {tokens(c)}
            <p className="lai-muted">
              첫 응답 {duration(c.firstByteMs)} · 전체 {duration(c.durationMs)}
            </p>
            {c.error && <p className="lai-error">{c.error}</p>}
          </article>
        ))}
      </div>
    </div>
  );
}
export type AiUsageTab = "사용량 탐색" | "호출 이력" | "요약" | "묶음 보기";
export function LlmUsage({
  view: provided,
  filters: controlled,
  onFiltersChange,
  onOpenRun,
  onLoadMore,
  loading = false,
  error,
  onRetry,
  example = "normal",
  initialTab = "사용량 탐색",
  explorer,
  explorerExample,
  onOpenExploreRun,
}: {
  view?: AiUsageView;
  filters?: AiFilters;
  onFiltersChange?: (filters: AiFilters) => void;
  onOpenRun?: (call: AiCall) => void;
  onLoadMore?: () => void;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  example?: AiExample;
  initialTab?: AiUsageTab;
  explorer?: UsageExplorerProps;
  explorerExample?: UsageExplorerExample;
  onOpenExploreRun?: UsageExplorerProps["onOpenRun"];
}) {
  const [local, setLocal] = useState(initialAiFilters),
    f = controlled ?? local;
  const [tab, setTab] = useState<"사용량 탐색" | "호출 이력">(
    initialTab === "호출 이력" ? "호출 이력" : "사용량 탐색",
  );
  const [limit, setLimit] = useState(8);
  // Host-controlled loading must never briefly display the preview response.
  // Navigation callbacks are also used by Storybook, so they are not a data boundary.
  const externalData =
    provided !== undefined ||
    explorer !== undefined ||
    controlled !== undefined ||
    onFiltersChange !== undefined ||
    onLoadMore !== undefined ||
    loading ||
    error !== undefined ||
    onRetry !== undefined;
  const view =
    provided ?? (externalData ? null : aiUsageFixture(f, example === "empty"));
  const change = (patch: Partial<AiFilters>) => {
    setLimit(8);
    const next = { ...f, ...patch };
    if (!controlled) setLocal(next);
    onFiltersChange?.(next);
  };
  return (
    <div className="lai-page" data-ai-usage>
      <div className="lai-inline">
        <SegmentedControl
          label="LLM 사용량 보기"
          options={[
            { value: "사용량 탐색", label: "사용량 탐색" },
            { value: "호출 이력", label: "호출 이력" },
          ]}
          value={tab}
          onChange={(v) => setTab(v as "사용량 탐색" | "호출 이력")}
        />
        <small className="lai-muted">
          한국 시간 기준 · 본문은 저장하지 않습니다
        </small>
      </div>
      {tab === "사용량 탐색" ? (
        explorer ? (
          <UsageExplorer {...explorer} />
        ) : externalData ? (
          <div className="ue-state" role="status">
            <Activity />
            <h3>사용량 탐색을 준비하고 있습니다</h3>
            <p>호출 이력에서 개별 요청을 확인할 수 있습니다.</p>
            <Button variant="secondary" onClick={() => setTab("호출 이력")}>
              호출 이력 보기
            </Button>
          </div>
        ) : (
          <UsageExplorerPreview
            example={
              explorerExample ??
              (example === "empty"
                ? "empty"
                : example === "error"
                  ? "error"
                  : "normal")
            }
            onOpenRun={onOpenExploreRun}
          />
        )
      ) : !view ? (
        <div className="ue-state" role="status">
          <Activity />
          <h3>호출 이력을 준비하고 있습니다</h3>
          <p>사용량 탐색에서 기간별·작업별 합계를 확인할 수 있습니다.</p>
          <Button variant="secondary" onClick={() => setTab("사용량 탐색")}>
            사용량 탐색 보기
          </Button>
        </div>
      ) : (
        <>
          <div className="lai-filters">
            <Field
              label="시작일"
              type="date"
              value={f.from}
              onChange={(e) => change({ from: e.target.value })}
            />
            <Field
              label="종료일"
              type="date"
              value={f.to}
              min={f.from}
              onChange={(e) => change({ to: e.target.value })}
            />
            <Select
              label="고객사"
              value={f.customerId}
              onChange={(e) => change({ customerId: e.target.value })}
            >
              <option value="all">전체 고객사 (실행·대상)</option>
              {view.customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Select
              label="머신"
              value={f.machineId}
              onChange={(e) => change({ machineId: e.target.value })}
            >
              <option value="all">전체 머신</option>
              {view.machines.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </Select>
            <Select
              label="모델"
              value={f.model}
              onChange={(e) => change({ model: e.target.value })}
            >
              <option value="all">전체 모델</option>
              {view.models.map((model) => (
                <option key={model}>{model}</option>
              ))}
            </Select>
            <Select
              label="실행 방식"
              value={f.mode}
              onChange={(e) => change({ mode: e.target.value })}
            >
              <option value="all">전체 방식</option>
              <option value="local">로컬 세션</option>
              <option value="relay">API 키</option>
            </Select>
            <Select
              label="호출 상태"
              value={f.status}
              onChange={(e) => change({ status: e.target.value })}
            >
              <option value="all">전체 상태</option>
              <option value="ok">성공</option>
              <option value="error">실패</option>
              <option value="cancelled">취소</option>
            </Select>
          </div>
          {f.from > f.to && (
            <p role="alert" className="lai-error">
              종료일은 시작일 이후로 선택해 주세요.
            </p>
          )}
          {loading && <p role="status">호출 이력을 읽고 있습니다…</p>}
          {error && (
            <p role="alert" className="lai-error">
              {error}
              {onRetry && (
                <Button variant="ghost" onClick={onRetry}>
                  다시 읽기
                </Button>
              )}
            </p>
          )}
          <section className="lai-panel">
            <header>
              <h3>호출 이력</h3>
              <span>{view.total}건</span>
            </header>
            <AiCallList
              calls={provided ? view.calls : view.calls.slice(0, limit)}
              onOpenRun={onOpenRun}
            />
            {(provided
              ? view.calls.length < view.total
              : limit < view.total) && (
              <Button
                variant="secondary"
                disabled={loading}
                onClick={() =>
                  provided ? onLoadMore?.() : setLimit((v) => v + 8)
                }
              >
                호출 더 보기
              </Button>
            )}
          </section>
          <p className="lai-muted lai-footnote">
            캐시는 입력에, 추론은 출력에 이미 포함됩니다. 비용은 기록 당시 모델
            단가 기준이며 실제 청구액과 다를 수 있습니다.
          </p>
        </>
      )}
    </div>
  );
}
export function MachineAiMode({
  view: provided,
  onSave,
  canEdit = false,
  example = "normal",
}: {
  view?: MachineAiView;
  onSave?: (mode: AiMode | null) => Promise<void>;
  canEdit?: boolean;
  example?: AiExample;
}) {
  const [fixture, setFixture] = useState(() => machineAiFixture(example)),
    v = provided ?? fixture;
  const [draft, setDraft] = useState<AiMode | "inherit">(v.mode ?? "inherit"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [note, setNote] = useState("");
  const effective = v.mode ?? v.defaultMode;
  return (
    <section className="lai-panel" data-ai-machine>
      <header>
        <div>
          <h3>AI 실행 방식</h3>
          <p>실행과 디바이스 조작은 이 PC에서 진행됩니다.</p>
        </div>
        <Badge>{effective === "local" ? "로컬 세션" : "API 키"}</Badge>
      </header>
      <div className="lai-mode-options">
        {(
          [
            {
              value: "inherit",
              title: "고객사 기본",
              text: `요금제 정책 따름 · ${v.defaultMode === "relay" ? "API 키" : "로컬 세션"}`,
              icon: <Activity />,
            },
            {
              value: "local",
              title: "로컬 세션",
              text: "이 PC에 로그인된 Codex 플랜",
              icon: <Monitor />,
            },
            {
              value: "relay",
              title: "API 키",
              text: "코어 중계 · 사용량 기준 서비스",
              icon: <KeyRound />,
            },
          ] as const
        ).map((o) => (
          <label
            key={o.value}
            className={draft === o.value ? "is-selected" : ""}
          >
            <input
              type="radio"
              name="machine-ai-mode"
              value={o.value}
              checked={draft === o.value}
              disabled={!canEdit || busy}
              onChange={() => setDraft(o.value)}
            />
            {o.icon}
            <strong>{o.title}</strong>
            <small>{o.text}</small>
          </label>
        ))}
      </div>
      {effective === "local" ? (
        <p className={v.loggedIn ? "lai-notice" : "lai-warning"}>
          {v.loggedIn
            ? "Codex 로그인됨 · 로컬 세션 사용 가능"
            : "Codex 로그인 필요 · 이 PC에서 로그인해 주세요"}
        </p>
      ) : (
        <>
          <RelayStatus view={v.readiness} />
          <p className={v.bridgeReady ? "lai-muted" : "lai-warning"}>
            {v.bridgeReady
              ? "브릿지가 API 키 중계를 지원합니다. 키 원문은 PC에 전달하지 않습니다."
              : "앱 업데이트 필요 · 현재 브릿지는 API 키 중계를 지원하지 않습니다."}
          </p>
        </>
      )}
      {error && (
        <p role="alert" className="lai-error">
          {error}
        </p>
      )}
      {note && <p role="status">{note}</p>}
      {canEdit ? (
        <DialogActions
          secondary={{
            label: "변경 취소",
            disabled: busy || draft === (v.mode ?? "inherit"),
            onClick: () => {
              setDraft(v.mode ?? "inherit");
              setError("");
            },
          }}
          primary={{
            label: busy ? "저장 중…" : "실행 방식 저장",
            disabled: busy || draft === (v.mode ?? "inherit"),
            onClick: async () => {
              setBusy(true);
              setError("");
              try {
                if (provided && !onSave)
                  throw new Error("현재 변경을 저장할 수 없습니다.");
                if (example === "error")
                  throw new Error(
                    "실행 방식 저장에 실패했습니다. 다시 시도해 주세요.",
                  );
                const mode = draft === "inherit" ? null : draft;
                await onSave?.(mode);
                if (!provided) setFixture({ ...v, mode });
                setNote("실행 방식을 저장했습니다. 다음 배정에 적용됩니다.");
              } catch (e) {
                setError(aiError(e));
              } finally {
                setBusy(false);
              }
            },
          }}
        />
      ) : (
        <p className="lai-muted">
          보기 전용 · 실행 방식은 관리자만 변경할 수 있습니다.
        </p>
      )}
    </section>
  );
}
